import { useEffect, useState } from 'react'
import { supabase, readableError } from '../lib/supabase'
import { isPasswordBreached } from '../lib/leakedPassword'
import { Field, Check } from './ui'

/**
 * How this account gets in, and how to change it.
 *
 * What it shows comes from my_auth_methods(), which reads the real bcrypt hash
 * and the real identities rows. The app used to keep a has_password flag in
 * user metadata — which the browser can write, so it was a claim rather than a
 * fact, and it was simply absent on any account not created by the current
 * signup form.
 */

const PROVIDER_LABEL = {
  google: ['Google', 'Signing in with your Google account'],
  apple: ['Apple', 'Signing in with your Apple ID'],
  email: ['Email', 'A code sent to your inbox, or your password'],
}

export default function Security() {
  const [methods, setMethods] = useState(null)
  const [open, setOpen] = useState(false)
  const [pw, setPw] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState(null)

  const load = () =>
    supabase.rpc('my_auth_methods').then(({ data }) => setMethods(data?.[0] ?? null))

  useEffect(() => { load() }, [])

  const save = async (e) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    const { breached } = await isPasswordBreached(pw)
    if (breached) {
      setBusy(false)
      return setErr('That password has appeared in a known data breach — pick a different one.')
    }
    const { error } = await supabase.auth.updateUser({ password: pw })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setPw(''); setOpen(false); setDone(true)
    load()
  }

  if (!methods) return null
  const providers = methods.providers ?? []

  return (
    <>
      <h3 className="sechead">How you sign in</h3>

      <div className="rows">
      {providers.map((p) => {
        const [name, hint] = PROVIDER_LABEL[p] ?? [p, 'Connected']
        return (
          <div className="methodrow" key={p}>
            <span className="mi" aria-hidden="true"><Check size={12} w={3} /></span>
            <span className="mt">{name}<small>{hint}</small></span>
          </div>
        )
      })}

      <div className="methodrow">
        <span className={`mi${methods.has_password ? '' : ' off'}`} aria-hidden="true">
          {methods.has_password ? <Check size={12} w={3} /> : '·'}
        </span>
        <span className="mt">
          Password
          <small>
            {methods.has_password
              ? 'Set. Your password manager can hold it.'
              : 'Not set — you sign in with an emailed code.'}
          </small>
        </span>
        <button className="iconbtn" onClick={() => { setOpen(!open); setDone(false) }}>
          {methods.has_password ? 'Change' : 'Set one'}
        </button>
      </div>

      {open && (
        /* A real form carrying the address, so a password manager has a
           username to file the credential under and will offer to save it.
           It lives inside the group, under the row that opened it. */
        <form className="rowbody" onSubmit={save}>
          <input type="text" name="username" autoComplete="username"
                 value={methods.email ?? ''} readOnly hidden aria-hidden="true" tabIndex={-1} />
          <Field label={methods.has_password ? 'New password' : 'Password'}>
            <div className="pwwrap">
              <input className="input" type={show ? 'text' : 'password'} name="password"
                     autoComplete="new-password" value={pw} autoFocus
                     placeholder="At least 8 characters"
                     onChange={(e) => setPw(e.target.value)} />
              <button type="button" className="pwpeek" onClick={() => setShow(!show)}
                      aria-pressed={show}
                      aria-label={show ? 'Hide password' : 'Show password'}>
                {show ? 'Hide' : 'Show'}
              </button>
            </div>
          </Field>
          <div className="rowbody-acts">
            <button type="submit" className="btn btn-primary" disabled={busy || pw.length < 8}>
              {busy ? 'Saving…' : 'Save password'}
            </button>
            <button type="button" className="btn btn-quiet"
                    onClick={() => { setOpen(false); setPw('') }}>
              Cancel
            </button>
          </div>
          {err && <p className="err">{err}</p>}
        </form>
      )}
      </div>

      {done && (
        <p className="small" style={{ color: 'var(--ok)', marginTop: -14, marginBottom: 26 }}>
          Password saved. Your password manager should offer to update it.
        </p>
      )}

      {!methods.has_password && providers.length <= 1 && (
        <p className="small" style={{ marginTop: -14, marginBottom: 26 }}>
          A password is optional. Without one you'll get an emailed code each time,
          which is just as secure — there's simply nothing to forget.
        </p>
      )}
    </>
  )
}
